import React, { useEffect, useState } from "react";
import { Modal, Button, Form } from "react-bootstrap";
import styles from "../../_css/TrainAIPopup.module.css";
import { Images } from "lucide-react";
import SelectComponent from "../common/SelectComponent";
import RichTextEditor from "./RichTextEditor";
import {
  CustomButton,
  CustomButtonLight,
  ToggleSwitch,
} from "../UI/CustomButtons";
import { startLoader, stopLoader } from "../../_store/reducers/loaderReducer";
import {
  deleteAuthToken,
  notifyError,
  notifySuccess,
} from "../../utils/helper";
import { useDispatch } from "react-redux";
import { fetchCategories, saveSession, updateTraining } from "../../requests";
import { formatCategories } from "../helper/formatters";
import { refreshSessionData } from "../../_store/reducers/refreshDataReducer";
import { setReviewSessionTitle } from "../../_store/reducers/reviewReducer";
import { formatHTMLResponse } from "../helper/formatters";

const defaultData = {
  title: "",
  title_arabic: "",
  description: "",
  description_arabic: "",
  image_url: "",
  mode: "text",
  ai_summary: "",
  status: 0,
  video_link: "",
  video_link_ar: "",
};

const TrainAIPopup = ({
  show,
  onHide,
  onOpenChat,
  data = defaultData,
  editMode = false,
  onOpenSummary,
  modelTitle = "Training AI",
}) => {
  const [category, setCategory] = useState(data?.category_id);
  const [titleEn, setTitleEn] = useState(data?.title);
  const [titleAr, setTitleAr] = useState(data?.title_arabic);
  const [descEn, setDescEn] = useState(data?.description);
  const [descAr, setDescAr] = useState(data?.description_arabic);
  const [image, setImage] = useState({
    file: null,
    preview: data?.image_url,
    name: data?.imageName,
  });
  const [mode, setMode] = useState(data?.mode);
  const [aiSummary, setAiSummary] = useState(data?.ai_summary);
  const [sessionVisible, setSessionVisible] = useState(data?.status === 1);
  const [isHover, setIsHover] = useState(false);
  const [categoriesOptions, setCategoriesOptions] = useState([]);
  const [language, setLanguage] = useState(data?.language);
  const [videoLink, setVideoLink] = useState(data?.video_link);
  const [videoLinkAr, setVideoLinkAr] = useState(data?.video_link_ar);

  const dispatch = useDispatch();

  useEffect(() => {
    // console.log("data", data);
    setCategory(data?.category_id);
    setTitleEn(data?.title);
    setTitleAr(data?.title_arabic);
    setDescEn(data?.description);
    setDescAr(data?.description_arabic);
    setSessionVisible(data?.status === 1);
    setAiSummary(data?.ai_summary);
    setVideoLink(data?.video_link);
    setVideoLinkAr(data?.video_link_ar);
    setLanguage(data?.language);  

    setImage({
      file: null,
      preview: data?.image_url,
      name: data?.imageName,
    });
  }, [data]);

  useEffect(() => {
    if (show) {
      getAllCategories();
      // Ensure image state is synced when modal opens
      setImage({
        file: null,
        preview: data?.image_url,
        name: data?.imageName,
      });
    } else {
      resetStates();
    }

    return () => {
      resetStates();
    };
  }, [modelTitle, show]);

  const resetStates = () => {
    setCategory(data?.category);
    setTitleEn(data?.title);
    setTitleAr(data?.title_arabic);
    setDescEn(data?.description);
    setDescAr(data?.description_arabic);
    setImage({
      file: null,
      preview: data?.image_url,
      name: data?.imageName || "image.png",
    });
    setMode(data?.mode);
    setAiSummary(data?.ai_summary);
    setSessionVisible(data?.status === 1);
    setLanguage(data?.language);
    setVideoLink(data?.video_link);
    setVideoLinkAr(data?.video_link_ar);
  };

  const handleImageChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];

      // Validate file type
      const allowedTypes = ["image/png", "image/jpeg", "image/jpg"];
      if (!allowedTypes.includes(file.type)) {
        notifyError("Please upload a valid image file (PNG, JPG, JPEG)");
        return;
      }

      // Validate file size (e.g., max 5MB)
      const maxSize = 5 * 1024 * 1024; // 5MB
      if (file.size > maxSize) {
        notifyError("Image size should be less than 5MB");
        return;
      }

      const createImageUrl = URL.createObjectURL(file);
      setImage({
        file: file,
        preview: createImageUrl,
        name: file.name,
      });
    }
  };

  const handleCategoryChange = (e) => {
    setCategory(e);
  };

  const getAllCategories = async () => {
    dispatch(startLoader());
    try {
      const response = await fetchCategories();

      setCategoriesOptions(formatCategories(response.data.categoryResult));
    } catch (error) {
      if (error?.response?.status === 401) {
        deleteAuthToken();
        window.location.reload();
      }
      console.error("Error in sending message:", error);
    }

    dispatch(stopLoader());
  };

  const validateFields = () => {
    if (!titleEn) {
      notifyError("Please enter the session title in English");
      return false;
    }
    
    if (!titleAr) {
      notifyError("Please enter the session title in Arabic");
      return false;
    }
    
    if (!descEn) {
      notifyError("Please enter the session description in English");
      return false;
    }
    
    if (!descAr) {
      notifyError("Please enter the session description in Arabic");
      return false;
    }
    
    if (!videoLink) {
      notifyError("Please enter the video link in English");
      return false;
    }
    
    if (!videoLinkAr) {
      notifyError("Please enter the video link in Arabic");
      return false;
    }
    
    if (!language) {
      notifyError("Please select a language");
      return false;
    }
    
    return true;
  }

  const saveUpdateSession = async (mode = "text", isUpdate = false) => {
    let isSuccess = false;
    if (!image.file && !image.preview) {
      notifyError("Please upload an image");
      return;
    }

    //check for category only if it is not update
    if (!isUpdate && !category) {
      notifyError("Please select a category");
      return;
    }

    // if (
    //   !titleAr ||
    //   !titleEn ||
    //   !descEn ||
    //   !descAr ||
    //   !videoLink ||
    //   !videoLinkAr ||
    //   !language 
    // ) {
    //   notifyError("Please fill all the fields");
    //   return;
    // }

    if (!validateFields()) {
      return;
    }

  
    dispatch(startLoader());
    try {
      const formData = new FormData();
      dispatch(setReviewSessionTitle(titleEn));

      formData.append("titleEn", titleEn);
      formData.append("titleAr", titleAr);
      formData.append("descEn", descEn);
      formData.append("descAr", descAr);
      formData.append("mode", mode);
      formData.append("sessionId", data?.id);
      formData.append("videoLink", videoLink);
      formData.append("language", language);
      formData.append("videoLinkAr", videoLinkAr);
      formData.append("sessionVisibility", sessionVisible);

      // Append the actual file if it exists, otherwise append the existing image URL
      if (image.file) {
        formData.append("image", image.file);
      } else if (image.preview) {
        // If we only have a preview URL (existing image), we need to handle this differently
        // For now, we'll just append the URL as a string
        formData.append("imageUrl", image.preview);
      }

      formData.append("categoryId", category?.value || category);
      let response;
      if (!isUpdate) {
        response = await saveSession(formData);
      } else {
        response = await updateTraining(formData);
      }

      if (response.status === 200) {
        // Success - continue with opening chat
        if (!isUpdate) {
          const sessionId = response.data.sessionId;
          sessionStorage.setItem("trainerSessionId", sessionId);
        }
        isSuccess = true;
      }
    } catch (error) {
      if (error?.response?.status === 401) {
        deleteAuthToken();
        window.location.reload();
      }
      console.error("Error in sending message:", error);
      notifyError("Failed to save session. Please try again.");
      isSuccess = false;
    }

    dispatch(stopLoader());
    return isSuccess;
  };

  const handleOpenChat = async (event, mode) => {
    event.preventDefault();
    const isSuccess = await saveUpdateSession(mode);
    if (isSuccess) {
      dispatch(refreshSessionData());
      setMode(mode);
      onOpenChat(mode);
      sessionStorage.setItem("trainingLanguage", language?.value);
    }
  };

  const handlePreviewSummary = (event, summary) => {
    event.preventDefault();
    onOpenSummary(summary, sessionVisible);
  };
  const handleSaveChanges = async (event) => {
    event.preventDefault();
    const isSuccess = await saveUpdateSession(mode, true);
    if (isSuccess) {
      notifySuccess("Session updated successfully");
      onHide();
      dispatch(refreshSessionData());
    }
  };
  const handleHidePopup = (event) => {
    event.preventDefault();
    onHide();
  };
  return (
    <Modal
      show={show}
      onHide={onHide}
      centered
      size="sm"
      className={styles.modal + " " + (editMode ? styles.editMode : "")}
    >
      <Modal.Header closeButton>
        <Modal.Title>{modelTitle}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form>
          {editMode && (
            <Form.Group className="mb-3">
              <div
                onMouseEnter={() => setIsHover(true)}
                onMouseLeave={() => setIsHover(false)}
                className="position-relative overflow-hidden"
              >
                {image.preview ? (
                  <img
                    style={{
                      borderRadius: "12px",
                      objectFit: "cover",
                    }}
                    width="100%"
                    height={200}
                    src={image.preview}
                    alt="session image"
                    onError={(e) => {
                      e.target.src = `${process.env.PUBLIC_URL}/img/no-image.jpeg`;
                    }}
                  />
                ) : (
                  <img
                    style={{
                      borderRadius: "12px",
                      objectFit: "cover",
                    }}
                    width="100%"
                    height={200}
                    src={`${process.env.PUBLIC_URL}/img/no-image.jpeg`}
                    alt="session image"
                  />
                )}
                <div
                  style={{
                    visibility: isHover ? "visible" : "hidden",
                    transform: isHover ? "translateY(0)" : "translateY(100%)",
                  }}
                  className={styles.uploadBoxHover}
                >
                  <input
                    type="file"
                    accept="image/png, image/jpeg, image/jpg"
                    onChange={handleImageChange}
                    className={styles.uploadInput}
                    id="upload-image"
                    hidden
                  />
                  <label htmlFor="upload-image" className={styles.uploadLabel}>
                    <div className={styles.uploadContent}>
                      <Images style={{ color: "#fff" }} />
                      <div>
                        <span className={styles.uploadTextHover}>
                          Click here to upload image (maximum size 5MB)
                        </span>
                        <div className={styles.uploadSubText}>
                          PNG, JPG, JPEG
                        </div>
                      </div>
                    </div>
                  </label>
                  {image && (
                    <div className={styles.imageName}>{image.name}</div>
                  )}
                </div>
              </div>
            </Form.Group>
          )}
          {!editMode && (
            <Form.Group className="mb-3">
              <label className={styles.formLabel}>Category*</label>
              <SelectComponent
                options={categoriesOptions}
                placeholder="Select Category"
                value={category}
                handleValueChange={handleCategoryChange}
                inputColumn="category"
                getSuggestionData={() => {}}
                clearValueHandler={() => {}}
                onFocusHandler={() => {}}
                isMulti={false}
                isClearable={false}
              />
            </Form.Group>
          )}
          {!editMode && (
            <Form.Group className="mb-3">
              <label className={styles.formLabel}>Language for Training*</label>
              <SelectComponent
                options={[
                  { label: "English", value: "en" },
                  { label: "Arabic", value: "ar" },
                ]}
                placeholder="Select Language"
                value={language}
                handleValueChange={setLanguage}
                inputColumn="language"
                getSuggestionData={() => {}}
                clearValueHandler={() => {}}
                onFocusHandler={() => {}}
                isMulti={false}
                isClearable={false}
              />
            </Form.Group>
          )}
          <Form.Group className="mb-3">
            <label className={styles.formLabel}>Session Title (English)*</label>
            <Form.Control
              type="text"
              value={titleEn}
              onChange={(e) => setTitleEn(e.target.value)}
              className={styles.input}
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <label className={styles.formLabel}>Session Title (Arabic)*</label>
            <Form.Control
              type="text"
              value={titleAr}
              onChange={(e) => setTitleAr(e.target.value)}
              className={styles.input}
              style={{ direction: "rtl" }}
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <RichTextEditor
              value={descEn}
              onChange={setDescEn}
              label="Description (English)*"
              labelClassName={styles.formLabel}
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <RichTextEditor
              value={descAr}
              onChange={setDescAr}
              placeholder="Enter session description in Arabic*"
              label="Description (Arabic)"
              labelClassName={styles.formLabel}
              isRTL={true}
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <label className={styles.formLabel}>Video Link (English)*</label>
            <Form.Control
              type="text"
              value={videoLink}
              onChange={(e) => setVideoLink(e.target.value)}
              className={styles.input}
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <label className={styles.formLabel}>Video Link (Arabic)*</label>
            <Form.Control
              type="text"
              value={videoLinkAr}
              onChange={(e) => setVideoLinkAr(e.target.value)}
              className={styles.input}
            />
          </Form.Group>

          {editMode && (
            <Form.Group className="mb-3">
              <label className={styles.formLabel}>
                Session Summary <span className="ai-tag">AI</span>
              </label>
              <p className={styles.summaryText}>
                <span
                  className=""
                  dangerouslySetInnerHTML={{
                    __html: formatHTMLResponse(aiSummary?.slice(0, 200)),
                  }}
                ></span>
                <CustomButtonLight
                  onClick={(e) => handlePreviewSummary(e, aiSummary)}
                  text="Preview full Summary"
                />
              </p>
            </Form.Group>
          )}
          {!editMode && (
            <Form.Group className="mb-3">
              <label className={styles.formLabel}>Upload session Image*</label>
              <div className={styles.uploadBox}>
                <input
                  type="file"
                  accept="image/png, image/jpeg, image/jpg"
                  onChange={handleImageChange}
                  className={styles.uploadInput}
                  id="upload-image"
                  hidden
                />

                <label htmlFor="upload-image" className={styles.uploadLabel}>
                  <div className={styles.uploadContent}>
                    <Images className={styles.uploadIcon} />
                    <div>
                      <span className={styles.uploadText}>
                        Click here to upload image(maximum size 5MB)
                      </span>
                      <div className={styles.uploadSubText}>PNG, JPG, JPEG</div>
                    </div>
                  </div>
                </label>

                {image?.name && (
                  <div className={styles.imageName}>{image.name}</div>
                )}
              </div>
            </Form.Group>
          )}
          {editMode && (
            <Form.Group
              className={`${styles.sessionVisiblity} mb-3 d-flex align-items-center justify-content-between`}
            >
              <label className={styles.formLabel}>Session Visibility</label>
              <ToggleSwitch
                value={sessionVisible}
                onChange={setSessionVisible}
              />
            </Form.Group>
          )}
          {editMode ? (
            <div className={styles.modeRow}>
              <label className={styles.formLabel}>
                Choose a mode to Train AI
              </label>
              <div className={styles.modeButtons}>
                <CustomButtonLight
                  onClick={(e) => handleHidePopup(e)}
                  text="Cancel"
                  width="48%"
                />
                <CustomButton
                  onClick={(e) => handleSaveChanges(e)}
                  text="Save Changes"
                  width="48%"
                />
              </div>
            </div>
          ) : (
            <div className={styles.modeRow}>
              <label className={styles.formLabel}>
                Choose a mode to Train AI
              </label>
              <div className={styles.modeButtons}>
                <CustomButtonLight
                  onClick={(e) => handleOpenChat(e, "text")}
                  text="Text"
                  width="48%"
                  icon="text"
                />
                <CustomButtonLight
                  onClick={(e) => handleOpenChat(e, "voice")}
                  text="Voice"
                  width="48%"
                  icon="voice"
                />
              </div>
            </div>
          )}
        </Form>
      </Modal.Body>
    </Modal>
  );
};

export default TrainAIPopup;
